import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { firstValueFrom, Subscription } from "rxjs";
import { IContentControlManager } from 'app/js/src/domain/content-control/manager';
import { IContentControlStudioTimeline } from "./content-control-studio-timeline/i-content-control-studio-timeline";
import { ContentControlCanvasService } from 'app/js/src/infrastructure/content-control/content-control-canvas.service';
import {
  ContentControlStudioActionsComponent,
} from "./content-control-studio-actions/content-control-studio-actions.component";
import { IEvrideoDialogService } from "app/js/src/domain/common/i-evrideo-dialog-service";
import { ContentControlStudioEventsComponent } from "./content-control-studio-events/content-control-studio-events.component";
import { MediaRepository } from "app/js/src/domain/media-repository";
import { ContentControlStudioVODViewModel } from "./content-control-studio-vod-view-model";
import { ContentControlStudioBaseViewModel } from "./content-control-studio-base-view-model";
import { ContentControlledLiveSettings } from "app/js/src/domain/content-control/settings/ContentControlledLiveSettings";
import { IContentControlVideoSource } from 'app/js/src/domain/content-control/i-content-control-video-source';
import { ContentControlVideoSource } from 'app/js/src/domain/content-control/content-control-video-source';
import { ContentControlStudioLiveViewModelV2 } from "./content-control-studio-live-view-model-v2";
@Component({
  selector: "evrideo-content-control-studio",
  templateUrl: "./content-control-studio.component.html",
  styleUrls: ["./content-control-studio.component.scss"],
})
export class ContentControlStudioComponent implements OnInit, AfterViewInit, OnDestroy {

  // --- Private fields ---
  private _eventsUpdatedSubscription: Subscription;
  private _selectedSegmentUpdatedSubscription: Subscription;
  private _itemUpdatedSubscription: Subscription;
  private _beforeUnloadHandler: (e: BeforeUnloadEvent) => void;
  private _liveTickInterval: ReturnType<typeof setInterval> | null = null;

  // --- Public fields ---
  public lastSeekedPosition = 0;
  public headerReady = false;
  public vm: ContentControlStudioBaseViewModel;
  public collectingEDL = false;
  public mediaId?: number;
  public channelUid?: string;
  public videoSource: IContentControlVideoSource;

  @ViewChild("videoContainer") public videoContainerRef: ElementRef<HTMLDivElement>;
  @ViewChild("loadingText") public loadingTextRef: ElementRef<HTMLHeadingElement>;
  @ViewChild('video', { static: true }) public videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild("canvas") public canvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild("drawingCanvas") public drawingCanvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild("timelineComponent", { static: false })
  public timelineRef!: IContentControlStudioTimeline;
  @ViewChild(ContentControlStudioActionsComponent)
  public actionsRef: ContentControlStudioActionsComponent;
  @ViewChild(ContentControlStudioEventsComponent)
  public eventsRef: ContentControlStudioEventsComponent;

  // --- Computed getters (not pure proxies) ---
  public get liveVM(): ContentControlStudioLiveViewModelV2 { return this.vm as ContentControlStudioLiveViewModelV2; }
  public get vodVM(): ContentControlStudioVODViewModel { return this.vm as ContentControlStudioVODViewModel; }
  public get isVOD() {
    return this.mediaId != null;
  }
  public get isLive() {
    return !this.isVOD;
  }

  // --- Constructor ---
  constructor(
    private _route: ActivatedRoute,
    public manager: IContentControlManager,
    private _dialogService: IEvrideoDialogService,
    private _cdr: ChangeDetectorRef,
    private _canvasService: ContentControlCanvasService,
    private _viewContainerRef: ViewContainerRef,
    private _mediaRepository: MediaRepository,
  ) {}

  // --- Lifecycle hooks ---
  public ngOnInit(): void {
    this._eventsUpdatedSubscription = this.manager.eventsUpdated.subscribe(
      () => this.vm?.eventsChangeDetected(),
    );
    this._selectedSegmentUpdatedSubscription =
      this.manager.selectedSegmentRemoved.subscribe((id) => {
        const segments = this.vm?.segments;
        if (!segments) return;
        const index = segments.findIndex((x) => x.id === id);
        if (index > -1) {
          segments.splice(index, 1);
        }
      });
    this._route.paramMap.subscribe(async (params) => {
      const key = params.get("id");
      if (key == null) {
        return;
      }
      const id = Number(key);
      if (!isNaN(id) && isFinite(id)) {
        this.mediaId = id;
        this.channelUid = undefined;
        this.manager.getItemByMediaId(id);
      } else {
        this.channelUid = key;
        this.mediaId = undefined;
        this.manager.getItemByChannelUid(key);
      }
    });
    this._beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (this.manager.hasChanges) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", this._beforeUnloadHandler);
  }

  public async ngAfterViewInit(): Promise<void> {
    try {
      await firstValueFrom(this.manager.itemUpdated);
    } catch {
      return;
    }

    this.timelineRef.setupTimeline(this.manager.videoFps);
    this.videoSource = new ContentControlVideoSource(this.videoRef.nativeElement);
    this.videoSource.setupFrameRate(this.manager.videoFps);
    if (this.mediaId) {
      this.vm = new ContentControlStudioVODViewModel(
        this._canvasService,
        this.videoSource,
        this,
      );
      this.manager.setLiveHlsProviderForScreenshotConversion?.(null);
    } else {
      this.vm = new ContentControlStudioLiveViewModelV2(
        this._canvasService,
        this.videoRef.nativeElement,
        this.timelineRef.oneFrameSecs,
        this.manager.videoUrl,
        this._mediaRepository,
        this.manager.itemSettings as ContentControlledLiveSettings,
        this,
        (this._route.snapshot.queryParams?.debug as boolean) || undefined,
        (this._route.snapshot.queryParams?.print as boolean) || undefined,
      );
      this.manager.setLiveHlsProviderForScreenshotConversion?.(this.liveVM.hlsVideoSource);
      this._liveTickInterval = setInterval(() => {
        this._cdr.detectChanges();
      }, 250);
    }
    this.vm.setupViewModel(
      this.manager,
      this._dialogService,
      this._viewContainerRef,
      this.timelineRef,
      this.eventsRef,
      this.videoContainerRef.nativeElement,
      this.loadingTextRef.nativeElement,
      this.videoSource,
      this.canvasRef,
      this.drawingCanvasRef,
      () => this._cdr.detectChanges(),
    );

    this._itemUpdatedSubscription = this.manager.itemUpdated.subscribe(() => {
      if (
        this.manager.selectedSegment &&
        this.manager.selectedSegment.isConfigurationComplete == false
      ) {
        return;
      }
      this.actionsRef.resetEverything();
    });

    this.headerReady = true;
  }

  public ngOnDestroy(): void {
    if (this._liveTickInterval) {
      clearInterval(this._liveTickInterval);
      this._liveTickInterval = null;
    }
    this.manager.setLiveHlsProviderForScreenshotConversion?.(null);
    this.vm?.destroy();
    this._eventsUpdatedSubscription?.unsubscribe();
    this._selectedSegmentUpdatedSubscription?.unsubscribe();
    this._itemUpdatedSubscription?.unsubscribe();
    window.removeEventListener("beforeunload", this._beforeUnloadHandler);
  }

  // --- Event handlers ---
  @HostListener("window:resize", ["$event"])
  public onWindowResize(_event?: Event): void {
    this.vm?.onWindowResize();
  }

  @HostListener("window:keydown", ["$event"])
  public onStudioKeyDown(event: KeyboardEvent): void {
    this.vm.onStudioKeyDown(event);
  }


}
